import * as React from 'react';
import {
    Button,
    DialogTitle,
    DialogActions,
    Dialog,
    DialogContent,
    Autocomplete,
    TextField,
    Typography,
    Box,
    Stack,
    IconButton,
    Chip,
    Divider,
    CircularProgress,
    Checkbox,
    FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import { useEffect, useState } from 'react';
import { getDefaultDashboardWidgetJsonSettings } from '../../utilities/DashboardUtilities';
import TaskApiService from '../../api/TaskApiService';

const taskApiService = new TaskApiService();
let cachedTaskStatuses = null;
let taskStatusesRequest = null;

const userAssignedTasksComponent = 'DashboardUserAssignedTasks';
const taskSummaryComponent = 'DashboardTaskSummary';

async function getCachedTaskStatuses() {
    if (cachedTaskStatuses) {
        return cachedTaskStatuses;
    }

    if (!taskStatusesRequest) {
        taskStatusesRequest = taskApiService.getTaskStatuses().then((statuses) => {
            cachedTaskStatuses = statuses;
            return statuses;
        });
    }

    return taskStatusesRequest;
}

function parseJsonSettings(jsonSettings) {
    try {
        if (!jsonSettings || jsonSettings === '{}') {
            return {};
        }

        return typeof jsonSettings === 'string' ? JSON.parse(jsonSettings) : jsonSettings;
    }
    catch (error) {
        console.error('Error parsing jsonSettings:', error);
        return {};
    }
}

function getExcludedStatusIdsFromJsonSettings(component, jsonSettings) {
    const settings = parseJsonSettings(jsonSettings || getDefaultDashboardWidgetJsonSettings(component));

    if (component !== userAssignedTasksComponent) {
        return [];
    }

    if (!Object.prototype.hasOwnProperty.call(settings, 'excludedStatusIds')) {
        return parseJsonSettings(getDefaultDashboardWidgetJsonSettings(component)).excludedStatusIds ?? [];
    }

    return Array.isArray(settings.excludedStatusIds) ? settings.excludedStatusIds : [];
}

function getTaskSummaryDateRangeFromJsonSettings(component, jsonSettings) {
    const settings = parseJsonSettings(jsonSettings || getDefaultDashboardWidgetJsonSettings(component));

    if (component !== taskSummaryComponent) {
        return {
            dateRangeEnabled: false,
            dateRange: { start: '', end: '' }
        };
    }

    const defaultSettings = parseJsonSettings(getDefaultDashboardWidgetJsonSettings(component));

    return {
        dateRangeEnabled: settings.dateRangeEnabled ?? defaultSettings.dateRangeEnabled ?? false,
        dateRange: {
            start: settings.dateRange?.start ?? defaultSettings.dateRange?.start ?? '',
            end: settings.dateRange?.end ?? defaultSettings.dateRange?.end ?? ''
        }
    };
}

const dashboardComponentOptions = [{
    name: 'Task Summary',
    component: 'DashboardTaskSummary',
    userCreated: false,
},
{
    name: 'User assigned tasks',
    component: 'DashboardUserAssignedTasks',
    userCreated: false,
},
{
    name: 'Risk Level',
    component: 'DashboardRiskLevel',
    userCreated: false,
}];

export default function DashboardWidgetModal(props) {
    const { open, position, onClose, onSave, setSnackbarProperties, widgetToEdit } = props;
    const [selectedComponent, setSelectedComponent] = useState(undefined);
    const [name, setName] = useState(undefined);
    const [taskStatuses, setTaskStatuses] = useState([]);
    const [taskStatusesLoading, setTaskStatusesLoading] = useState(false);
    const [excludedStatusIds, setExcludedStatusIds] = useState([]);
    const [taskSummaryDateRangeEnabled, setTaskSummaryDateRangeEnabled] = useState(false);
    const [taskSummaryDateRange, setTaskSummaryDateRange] = useState({ start: '', end: '' });

    const selectedExcludedStatuses = taskStatuses.filter((status) => excludedStatusIds.includes(status.stId));

    function handleSave() {
        if (!selectedComponent) {
            setSnackbarProperties('Component is required', 'error');
            return;
        }

        if (!name) {
            setSnackbarProperties('Name is required', 'error');
            return;
        }

        const jsonSettings = getJsonSettingsForSelectedComponent();

        let dashboardComponent;
        if (widgetToEdit) {
            dashboardComponent = {
                ...widgetToEdit,
                position: position,
                name: name,
                component: selectedComponent.component,
                jsonSettings: jsonSettings,
            };
        }
        else if (!selectedComponent.userCreated) {
            const componentId = position + 1;
            dashboardComponent = {
                id: `box${componentId}`,
                position: position,
                name: name,
                component: selectedComponent.component,
                jsonSettings: jsonSettings,
            };
        }
        else {
            dashboardComponent ={
                ...selectedComponent,
                position: position,
                jsonSettings: jsonSettings,
            }
        }

        onSave(dashboardComponent);
    }

    function getJsonSettingsForSelectedComponent() {
        const existingJsonSettings = widgetToEdit?.component === selectedComponent.component
            ? widgetToEdit.jsonSettings || getDefaultDashboardWidgetJsonSettings(selectedComponent.component)
            : getDefaultDashboardWidgetJsonSettings(selectedComponent.component);
        const settings = parseJsonSettings(existingJsonSettings);

        if (selectedComponent.component === userAssignedTasksComponent) {
            return JSON.stringify({
                ...settings,
                excludedStatusIds: excludedStatusIds,
            });
        }

        if (selectedComponent.component === taskSummaryComponent) {
            return JSON.stringify({
                ...settings,
                dateRangeEnabled: taskSummaryDateRangeEnabled,
                dateRange: taskSummaryDateRange
            });
        }

        return existingJsonSettings;
    }

    function handleSelectedComponentChange(newValue) {
        setSelectedComponent(newValue);
        setName(newValue?.name);
        setExcludedStatusIds(getExcludedStatusIdsFromJsonSettings(newValue?.component, getDefaultDashboardWidgetJsonSettings(newValue?.component)));
        const taskSummarySettings = getTaskSummaryDateRangeFromJsonSettings(newValue?.component, getDefaultDashboardWidgetJsonSettings(newValue?.component));
        setTaskSummaryDateRangeEnabled(taskSummarySettings.dateRangeEnabled);
        setTaskSummaryDateRange(taskSummarySettings.dateRange);
    }

    useEffect(() => {
        if (open) {
            if (widgetToEdit) {
                const matchedComponent = dashboardComponentOptions.find(
                    (option) => option.component === widgetToEdit.component
                );

                setName(widgetToEdit.name);
                setSelectedComponent(matchedComponent);
                setExcludedStatusIds(getExcludedStatusIdsFromJsonSettings(widgetToEdit.component, widgetToEdit.jsonSettings));
                const taskSummarySettings = getTaskSummaryDateRangeFromJsonSettings(widgetToEdit.component, widgetToEdit.jsonSettings);
                setTaskSummaryDateRangeEnabled(taskSummarySettings.dateRangeEnabled);
                setTaskSummaryDateRange(taskSummarySettings.dateRange);
                return;
            }

            setName(undefined);
            setSelectedComponent(undefined);
            setExcludedStatusIds([]);
            setTaskSummaryDateRangeEnabled(false);
            setTaskSummaryDateRange({ start: '', end: '' });
        }
    }, [open, widgetToEdit]);

    useEffect(() => {
        if (!open || selectedComponent?.component !== userAssignedTasksComponent) {
            return;
        }

        let ignore = false;
        setTaskStatusesLoading(true);

        getCachedTaskStatuses()
            .then((statuses) => {
                if (!ignore) {
                    setTaskStatuses(statuses);
                }
            })
            .finally(() => {
                if (!ignore) {
                    setTaskStatusesLoading(false);
                }
            });

        return () => {
            ignore = true;
        };
    }, [open, selectedComponent]);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                elevation: 8,
                sx: {
                    borderRadius: 3,
                    minHeight: '500px'
                }
            }}
        >
            <DialogTitle sx={{ pb: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <DashboardCustomizeIcon sx={{ fontSize: 28, color: '#99C20E' }} />
                        <Box>
                            <Typography variant="h5" fontWeight="600">
                                {widgetToEdit ? 'Edit Widget' : 'Add Widget'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {widgetToEdit ? 'Update the widget shown on your dashboard' : 'Choose a widget to add to your dashboard'}
                            </Typography>
                        </Box>
                    </Box>
                    <IconButton
                        onClick={onClose}
                        sx={{
                            color: 'text.secondary',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ py: 3 }}>
                <Stack spacing={3}>
                    <Box>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                            Select Widget Type
                        </Typography>
                        <Autocomplete
                            options={dashboardComponentOptions}
                            value={selectedComponent || null}
                            onChange={(event, newValue) => {
                                handleSelectedComponentChange(newValue);
                            }}
                            getOptionLabel={(option) => option?.name || 'Unknown Widget'}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Choose a widget"
                                    variant="outlined"
                                    placeholder="Search widgets..."
                                />
                            )}
                            renderOption={(props, option) => (
                                <Box component="li" {...props}>
                                    <Stack direction="row" alignItems="center" spacing={2} sx={{ width: '100%' }}>
                                        <Box sx={{ flex: 1 }}>
                                            <Typography variant="body1" fontWeight="500">
                                                {option.name}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary">
                                                {option.component}
                                            </Typography>
                                        </Box>
                                        <Chip
                                            label={option.userCreated ? 'Custom' : 'Default'}
                                            size="small"
                                            color={option.userCreated ? 'primary' : 'default'}
                                            variant="outlined"
                                        />
                                    </Stack>
                                </Box>
                            )}
                            isOptionEqualToValue={(option, value) => option.name === value.name}
                        />
                    </Box>

                    <Box>
                        <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                            Widget Name
                        </Typography>
                        <TextField
                            value={name || ''}
                            label="Enter widget name"
                            onChange={(event) => setName(event.target.value)}
                            fullWidth
                            variant="outlined"
                            placeholder="My Dashboard Widget"
                            helperText="This name will appear on your dashboard"
                        />
                    </Box>

                    {selectedComponent?.component === userAssignedTasksComponent && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                                Excluded Task Statuses
                            </Typography>
                            <Autocomplete
                                multiple
                                options={taskStatuses}
                                value={selectedExcludedStatuses}
                                loading={taskStatusesLoading}
                                onChange={(event, newValue) => {
                                    setExcludedStatusIds(newValue.map((status) => status.stId));
                                }}
                                getOptionLabel={(option) => option?.stName || ''}
                                isOptionEqualToValue={(option, value) => option.stId === value.stId}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Excluded statuses"
                                        variant="outlined"
                                        placeholder="Select statuses"
                                        helperText="Tasks with these statuses will be hidden from this widget"
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {taskStatusesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Box>
                    )}

                    {selectedComponent?.component === taskSummaryComponent && (
                        <Box>
                            <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2 }}>
                                Task Date Range
                            </Typography>
                            <Stack spacing={2}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={taskSummaryDateRangeEnabled}
                                            onChange={(event) => setTaskSummaryDateRangeEnabled(event.target.checked)}
                                        />
                                    }
                                    label="Filter task summary by reported date"
                                />
                                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                                    <TextField
                                        label="Start Date"
                                        type="date"
                                        value={taskSummaryDateRange.start}
                                        onChange={(event) => {
                                            setTaskSummaryDateRange({
                                                ...taskSummaryDateRange,
                                                start: event.target.value
                                            });
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        disabled={!taskSummaryDateRangeEnabled}
                                    />
                                    <TextField
                                        label="End Date"
                                        type="date"
                                        value={taskSummaryDateRange.end}
                                        onChange={(event) => {
                                            setTaskSummaryDateRange({
                                                ...taskSummaryDateRange,
                                                end: event.target.value
                                            });
                                        }}
                                        InputLabelProps={{ shrink: true }}
                                        fullWidth
                                        disabled={!taskSummaryDateRangeEnabled}
                                    />
                                </Stack>
                            </Stack>
                        </Box>
                    )}
                </Stack>
            </DialogContent>

            <Divider />

            <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="inherit"
                    sx={{ minWidth: 100 }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                        minWidth: 120,
                        backgroundColor: '#99C20E',
                        '&:hover': { backgroundColor: '#76960c' }
                    }}
                    disabled={!selectedComponent || !name}
                >
                    {widgetToEdit ? 'Save Widget' : 'Add Widget'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
